import React from "react";
import {
  LinkedinLogo,
  Microphone,
  GraduationCap,
  Briefcase,
  Compass,
  Trophy,
  ArrowUpRight,
  RocketLaunch,
} from "@phosphor-icons/react";
import { Reveal } from "../lib/motion";
import { Eyebrow } from "../components/ui-kit";

const BLOCKS = [
  {
    icon: RocketLaunch,
    title: "Entrepreneurship",
    text: "Serial entrepreneur dedicated to building businesses with lasting impact. From AI-powered human growth (InTribe.ai) to innovation-led consumer brands (Masols International) and trusted collectibles commerce (CollectIQ), every venture is built to solve meaningful problems at scale.",
  },
  {
    icon: Briefcase,
    title: "Experience",
    text: "Over two decades across pharmaceuticals, FMCG, telecom, and SaaS-LMS — from management trainee to P&L head. A career spent building brands, growing revenue, and leading teams through change.",
  },
  {
    icon: Compass,
    title: "Leadership Philosophy",
    text: "Execution over presentations. Founder-first thinking. A deep love for creative problem-solving, hands-on leadership, and genuine customer delight — blending Indian spirit with a world-ready mindset.",
  },
  {
    icon: Microphone,
    title: "Speaking",
    text: "A recognised voice on company building, revenue growth, and entrepreneurship — sharing practical frameworks that help founders move from startup to scalable organization.",
  },
  {
    icon: GraduationCap,
    title: "Mentoring",
    text: "Mentor, Executive Board Member, and South Chapter Chair of the All-India Management Association (AIMA) - YLC (Young Leaders Council) — supporting emerging leaders and shaping the next generation of managers and entrepreneurs.",
  },
];

export default function AboutNirmalya() {
  return (
    <div data-testid="about-nirmalya-page">
      {/* Hero */}
      <section className="bg-cloud pt-40 pb-20 lg:pt-48 lg:pb-24">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <div className="grid items-end gap-14 lg:grid-cols-[1.1fr_0.9fr]">
            <Reveal>
              <Eyebrow>About Nirmalya</Eyebrow>
              <h1 className="mt-7 font-heading text-5xl font-light leading-[1.02] tracking-tight text-navy sm:text-6xl lg:text-[4.2rem]">
                Nirmalya <span className="font-semibold text-gold">Banerjee</span>
              </h1>
              <p className="mt-6 text-lg leading-relaxed text-slate-600">
                Business leader, entrepreneur, startup mentor, and company builder with
                over two decades of experience in strategy, revenue growth, business
                transformation, and leadership.
              </p>
              <div className="mt-7 inline-flex items-center gap-2 rounded-full bg-gold/12 px-4 py-2 text-sm font-semibold text-navy">
                <Trophy size={18} weight="fill" className="text-gold" />
                One of India's “Top 10 Iconic CSOs”, 2024
              </div>
              <div className="mt-8">
                <a
                  href="https://www.linkedin.com/in/nirmalya-revenue-architect"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="nirmalya-linkedin"
                  className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                >
                  <LinkedinLogo size={18} weight="fill" /> Connect on LinkedIn
                  <ArrowUpRight size={15} weight="bold" />
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.12} className="relative">
              <div className="overflow-hidden rounded-[28px] shadow-[0_30px_80px_rgba(11,31,58,0.2)]">
                <img
                  src="/images/nirmalya2.png"
                  alt="Nirmalya Banerjee"
                  className="aspect-[4/5] w-full object-cover object-top"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Bio detail */}
      <section className="bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal className="mx-auto max-w-3xl">
            <p className="font-heading text-2xl font-light leading-snug tracking-tight text-navy sm:text-[1.9rem]">
              Through Founder+, Nirmalya partners with ambitious founders to help them
              build scalable businesses through practical execution, systems thinking,
              and long-term growth.
            </p>
            <p className="mt-8 text-lg leading-relaxed text-slate-600">
              A management graduate and executive alumnus of ISB Hyderabad, he brings
              homegrown insight and international perspective to everything he does —
              grounded in a genuine belief that every founder deserves more than
              advice. They deserve someone who helps them build.
            </p>
          </Reveal>

          <div className="mt-20 grid gap-6 sm:grid-cols-2">
            {BLOCKS.map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal
                  key={b.title}
                  delay={(i % 2) * 0.08}
                  className="rounded-3xl border border-slate-100 bg-cloud/60 p-9 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(11,31,58,0.08)]"
                >
                  <div className="grid h-14 w-14 place-items-center rounded-2xl bg-teal/10 text-teal">
                    <Icon size={26} weight="duotone" />
                  </div>
                  <h3 className="mt-6 font-heading text-2xl font-semibold text-navy">
                    {b.title}
                  </h3>
                  <p className="mt-3 leading-relaxed text-slate-600">{b.text}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
